import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import {
  aggregateSupplierStandardsByCode,
  getSupplierStandardRows,
  supplierOverallPercent,
} from '../../utils/supplierStandardsDisplay'
import entStyles from '../../styles/enterprise/EnterpriseOperationalUx.module.scss'

export type HomeSupplierRecord = {
  id?: string
  name?: string
  compliancePercentage?: number
  compliancePct?: number
  percent?: number
  compliance?: number
  standards?: Array<string | { code?: string; standardCode?: string; percent?: number; compliancePercentage?: number }>
  standardsCompliance?: Array<{ code?: string; percent?: number | null }>
}

type Props = {
  suppliers: HomeSupplierRecord[]
}

function supplierName(row: HomeSupplierRecord, index: number): string {
  return String(row.name || row.id || `Supplier ${index + 1}`)
}

function percentBadgeClass(percent: number): string {
  if (percent >= 85) return entStyles.badgeStable
  if (percent >= 40) return entStyles.badgeWarn
  return entStyles.badgeRisk
}

function StandardRowsList({
  rows,
  t,
}: {
  rows: Array<{ code: string; percent: number | null }>
  t: ReturnType<typeof useI18n>['t']
}) {
  if (!rows.length) {
    return (
      <p className={entStyles.emptyState}>
        {t('home.suppliers.noStandardsAssigned', 'No standards assigned yet.')}
      </p>
    )
  }

  return (
    <ul className={entStyles.supplierStandardsList}>
      {rows.map((row) => (
        <li key={row.code}>
          <span>{getStandardDisplayName(t, row.code)}</span>
          <strong className={row.percent == null ? entStyles.complianceMissing : undefined}>
            {row.percent == null
              ? t('home.suppliers.notAssessed', 'Not assessed')
              : `${row.percent}%`}
          </strong>
        </li>
      ))}
    </ul>
  )
}

export default function HomeSuppliersExecutiveCard({ suppliers }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const list = useMemo(() => suppliers.filter(Boolean), [suppliers])
  const count = list.length

  const aggregatedStandards = useMemo(
    () => (count > 2 ? aggregateSupplierStandardsByCode(list) : []),
    [count, list],
  )

  const averageOverall = useMemo(() => {
    if (!count) return 0
    const vals = list.map(supplierOverallPercent)
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length)
  }, [count, list])

  return (
    <section
      className={`${entStyles.layer} ${entStyles.onDarkSurface}`}
      data-testid="home-suppliers-posture"
    >
      <p className={entStyles.layerLabel}>
        {t('home.suppliers.layerLabel', 'Third-party risk & supplier compliance')}
      </p>
      <div className={entStyles.posturePanel}>
        <h2 className={entStyles.postureTitle}>{t('sections.suppliers', 'Suppliers')}</h2>
        <p className={entStyles.postureSubtitle}>
          {t('home.suppliers.subtitle')}
        </p>

        {!count ? (
          <p className={entStyles.emptyState}>
            {t('home.suppliers.none', 'No suppliers registered yet.')}
          </p>
        ) : count > 2 ? (
          <>
            <div className={entStyles.postureGrid}>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('home.suppliers.participantCount', 'Participants')}
                </p>
                <p className={entStyles.postureMetricValue}>{count}</p>
              </div>
              <div className={entStyles.postureMetric}>
                <p className={entStyles.postureMetricLabel}>
                  {t('home.suppliers.avgCompliance', 'Average compliance')}
                </p>
                <p className={entStyles.postureMetricValue}>{averageOverall}%</p>
              </div>
            </div>
            <StandardRowsList rows={aggregatedStandards} t={t} />
          </>
        ) : (
          <div className={entStyles.supplierDetailStack}>
            {list.map((supplier, index) => {
              const rows = getSupplierStandardRows(supplier)
              const overall = supplierOverallPercent(supplier)
              return (
                <div key={String(supplier.id || index)} className={entStyles.supplierDetailBlock}>
                  <div className={`${entStyles.postureMetric} ${entStyles.supplierDetailMetric}`}>
                    <div className={entStyles.supplierDetailHeader}>
                      <h3 className={entStyles.supplierDetailName}>{supplierName(supplier, index)}</h3>
                      <span className={percentBadgeClass(overall)}>{overall}%</span>
                    </div>
                    <StandardRowsList rows={rows} t={t} />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {count > 0 ? (
          <button
            type="button"
            className={entStyles.linkAction}
            onClick={() => navigate('/tpsm/inventory')}
          >
            {t('home.suppliers.openRegistry', 'Open supplier registry')}
          </button>
        ) : null}
      </div>
    </section>
  )
}
